<template>
  <NebulaLayout v-if="user">
    <div class="studio-home">
      <HomeSidebar />
      <div class="studio-home__main">
        <div
          v-if="pageHeader"
          class="studio-home__header-wrapper"
        >
          <h1 class="studio-home__header">
            {{ pageHeader }}
          </h1>
        </div>
        <div class="studio-home__main-inner-wrap">
          <BoardFilters :sidebar-filter="sidebarFilter">
            <h3
              v-if="sidebarFilter === 'TRASH'"
              class="studio-home__trash-header"
            >
              {{ $t('You can restore your creations.') }}
            </h3>
          </BoardFilters>

          <!-- Error state -->
          <CometBlockMessage
            block-message-type="error"
            v-if="error"
          >
            <p>{{ error }}</p>
          </CometBlockMessage>

          <!-- Loading state -->
          <LoadingIndicator
            v-else-if="loading"
            class="studio-home__loading"
            transparent
          />

          <!-- Empty state (hidden for now behind the Studio Templates product setting) -->
          <HomeEmptyState
            v-else-if="boards && !boards.length"
            :sidebar-filter="sidebarFilter"
          />

          <!-- Has-boards state -->
          <template v-else>
            <BoardList
              :boards="boards"
              :layout="query.layout"
              :sidebar-filter="sidebarFilter"
            />
            <NebulaPagination
              :link-generator="linkGenerator"
              responsive-dropdown-view
            />
          </template>
        </div>
      </div>
    </div>
  </NebulaLayout>
</template>

<script>
import {
  NebulaPagination,
  NebulaPaginationLinkGenerators,
} from '@discoveryedu/nebula-components';
import { mapState, mapActions } from 'pinia';
import { bus } from '@/lib/eventBus';
import CometBlockMessage from '@/components/ui-elements/CometBlockMessage.vue';
import BoardFilters from '@/components/home/BoardFilters.vue';
import BoardList from '@/components/home/BoardList.vue';
import HomeEmptyState from '@/components/home/HomeEmptyState.vue';
import HomeSidebar from '@/components/home/HomeSidebar.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import NebulaLayout from '@/components/navigation/NebulaLayout.vue';
import homeMixin from '@/mixins/home';
import * as types from '@/lib/constants/store';
import {
  useHomeStore,
  useUserStore,
} from '@/stores';

class StudioHomeLinkGenerator extends NebulaPaginationLinkGenerators.PageGenerator {
  getPageLink(pageNumber) {
    // PageGenerator already has everything we need, except it adds a
    // per_page query param that we don't need. This adds only
    // the page query param
    return this.getUrlWithQueryParams({
      page: pageNumber,
    });
  }
}

export default {
  name: 'StudioHome',
  components: {
    BoardFilters,
    BoardList,
    HomeEmptyState,
    HomeSidebar,
    LoadingIndicator,
    NebulaLayout,
    NebulaPagination,
    CometBlockMessage,
  },
  mixins: [homeMixin],
  props: {
    sidebarFilter: {
      type: String,
      required: true, // this is passed by the router
    },
  },
  computed: {
    ...mapState(useUserStore, [
      'productSettings',
      'user',
      'userIsStudent',
    ]),
    ...mapState(useHomeStore, [
      'boards',
      'error',
      'loading',
      'totalPages',
    ]),
    linkGenerator() {
      return new StudioHomeLinkGenerator({
        baseUrl: `${window.location.pathname}${window.location.search}`,
        page: Number(this.query.page),
        perPage: 20,
        totalRecords: this.totalPages * 20,
        usePushState: false,
        useRouterLink: true,
      });
    },
    pageHeader() {
      const pageHeaders = {
        MY_STUDIO: this.$t('My Studio'),
        PUBLISHED: this.$t('Published'),
        ASSIGNED: this.$t('Assigned'),
        SHARED_WITH_ME: this.$t('Shared with Me'),
        TRASH: this.$t('Trash'),
      };

      return pageHeaders[this.sidebarFilter];
    },
  },
  created() {
    this.getBoards();

    if (this.user) {
      // Students no longer have access to the Assigned filter; send them back to Studio Home
      if (this.userIsStudent && this.sidebarFilter === 'ASSIGNED') {
        this.$router.push({ name: 'home' });
      }
    }
    bus.on('deleteBoard', this.onBoardDeleted);
  },
  beforeUnmount() {
    bus.off('deleteBoard', this.onBoardDeleted);
  },
  watch: {
    sidebarFilter: 'getBoards',
    $route: 'onRouteChange',
  },
  methods: {
    ...mapActions(useHomeStore, [types.GET_HOME_BOARDS]),
    onBoardDeleted() {
      /*
        If the current page only has one item and it gets deleted,
        then we need to go back one page before refreshing.
        Otherwise, just refresh the current page.
      */
      const currentPage = Number(this.query.page);
      if (currentPage > 1 && this.boards.length === 1) {
        this.pushQuery({
          ...this.query,
          page: currentPage - 1,
        });
      } else {
        this.getBoards();
      }
    },
    onRouteChange(to, from) {
      if (to.name !== from.name) return;
      if (this.queryHasChanged(to, from)) {
        this.getBoards();
      }
    },
    async getBoards() {
      await this[types.GET_HOME_BOARDS]({
        user: this.user,
        sidebarFilter: this.sidebarFilter,
        query: this.query,
      });
      window.scrollTo(0, 0);
    },
  },
};
</script>

<style lang="stylus">
@import '../styles/home.styl';
</style>
